import type { SystemStyleObject } from '@chakra-ui/react';
import type {
	ControlProps,
	DropdownIndicatorProps,
	GroupBase,
	OptionBase,
	OptionProps,
	PlaceholderProps,
} from 'chakra-react-select';
import type { ChakraStylesProps } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import { colors } from '@/components/ui/theme';

export const getChakrastyles = <
	Option extends OptionBase = OptionBase,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>,
>({
	borderRadius,
	solidGradiant,
	ai,
	isMulti,
	error,
	color,
	bg,
	borderColor,
	rounded,
}: ChakraStylesProps) => ({
	multiValue: (provided: SystemStyleObject) => ({
		...provided,
		bg: 'brand.solid',
		borderRadius: rounded ? borderRadius : 4,
		px: 2,
	}),
	multiValueLabel: (provided: SystemStyleObject) => ({
		...provided,
		color: 'white',
		fontSize: 'xs',
		fontWeight: 'normal',
	}),
	multiValueRemove: (provided: SystemStyleObject) => ({
		...provided,
		color: 'white',
	}),
	dropdownIndicator: (
		provided: SystemStyleObject,
		{
			selectProps: { menuIsOpen },
		}: DropdownIndicatorProps<Option, IsMulti, Group>,
	) => ({
		...provided,
		color,
		...(ai || error
			? {}
			: {
					'& > svg': {
						transitionProperty: 'transform',
						transitionDuration: 'moderate',
						transform: `rotate(${menuIsOpen ? -180 : 0}deg)`,
						color:
							menuIsOpen && !rounded
								? 'brand.inputText'
								: menuIsOpen && rounded
									? 'white'
									: provided.color,
					},
				}),
	}),
	control: (
		provided: SystemStyleObject,
		state: ControlProps<Option, IsMulti, Group>,
	) => ({
		...provided,
		outline: 'none',
		borderRadius: isMulti ? 'none' : rounded ? borderRadius : 4,
		borderColor: error
			? 'brand.errors'
			: state.selectProps.menuIsOpen
				? 'transparent'
				: borderColor || provided.borderColor,
		borderWidth: isMulti || !rounded ? 0 : 1,
		borderBottomWidth: isMulti || !rounded ? 2 : 1,
		borderImage:
			isMulti || (!rounded && !error) ? `${solidGradiant} 1` : undefined,
		bg:
			isMulti || !rounded
				? 'brand.inputBg'
				: state.selectProps.menuIsOpen
					? `${solidGradiant}`
					: bg,
		color:
			isMulti || !rounded
				? color
				: state.selectProps.menuIsOpen
					? 'white'
					: color,
	}),
	placeholder: (
		provided: SystemStyleObject,
		state: PlaceholderProps<Option, IsMulti, Group>,
	) => ({
		...provided,
		fontSize: 'sm',
		display: 'flex',
		alignItems: 'center',
		color:
			isMulti || !rounded
				? 'brand.inputPlaceholder'
				: state.selectProps.menuIsOpen
					? 'white'
					: color,
		gap: 1,
	}),
	menu: (provided: SystemStyleObject) => ({
		...provided,
		my: 0,
	}),
	menuList: (provided: SystemStyleObject) => ({
		...provided,
		borderRadius: rounded ? borderRadius : 4,
		overflow: 'auto',
		scrollbarWidth: 'thin',
		scrollbarColor: `{brand.muted} transparent`,
		boxShadow: `0px 4px 4px 0px ${colors.menuListShadow}`,
	}),
	option: (
		provided: SystemStyleObject,
		state: OptionProps<Option, IsMulti, Group>,
	) => ({
		...provided,
		borderRadius: rounded ? borderRadius : 4,
		py: isMulti ? 1.5 : 1,
		bg: state.isFocused ? 'brand.solidGradient' : '',
		color: state.isFocused
			? 'brand.muted'
			: !state.isFocused && state.isSelected
				? color
				: provided.color,
		'&:active': {
			backgroundColor: 'brand.solidGradient !important',
			color: 'brand.muted !important',
		},
	}),
});
